import { LINKS } from './links'

/** Controls project card visibility: 'public' shows on all environments, 'internal' shows on dev only, 'college' groups academic projects separately. */
export type ProjectVisibility = 'public' | 'internal' | 'college'

/** Types of external links a project can have. */
export type ProjectLinkType = 'github' | 'playstore' | 'appstore' | 'company'

/** A single external link for a project with an optional fallback URL if the primary link breaks. */
export type ProjectLink = {
  type: ProjectLinkType
  url: string
  fallbackUrl?: string
  /** Display name for tooltips (e.g. "Extramarks", "MadOverGames"). Falls back to generic type label if omitted. */
  label?: string
}

/** Defines a portfolio project entry with metadata, visibility controls, links, and display ordering. */
export type ProjectDef = {
  /** Unique project identifier (kebab-case), used for anchors and routing */
  id: string
  /** Uppercase key matching the debug toggle in debug.ts (e.g. "PVP_MULTIPLAYER") */
  debugKey: string
  /** Display title shown on the project card */
  title: string
  /** Description text shown on the project card */
  desc: string
  /** Technology stack labels displayed as tags */
  tech: string[]
  /** Available external links (GitHub, Play Store, App Store, company page, etc.) */
  links?: ProjectLink[]
  /** Visibility scope: public, internal (dev-only), or college */
  visibility: ProjectVisibility
  /** Optional badge text (e.g. "Company IP - Extramarks", "Top 5 Finalist") */
  badge?: string
  /** Reason text shown in popup when hovering over lock icon (only for internal/company projects) */
  lockReason?: string
  /** Optional image path for the project card thumbnail */
  image?: string
  /** Whether this project is rendered on the page */
  enabled: boolean
  /** Sort order (lower values appear first) */
  order: number
}

/** Full portfolio project catalog grouped into: senior-level work (order 0-2), public side projects (order 3-4), and college projects (order 5-8). */
export const projectsConfig: ProjectDef[] = [
  // === Senior-level projects (order 0-2) ===
  {
    id: 'pvp-multiplayer',
    debugKey: 'PVP_MULTIPLAYER',
    title: 'PvP Multiplayer Match Engine',
    desc: 'Real-time player-vs-player matchmaking system with MMR-based algorithms, WebSocket state synchronization, and same-seed room matchmaking. Integrated Photon and custom WebSocket for seamless multiplayer experiences.',
    tech: ['Unity', 'C#', 'WebSocket', 'Firebase', 'MMR'],
    links: [
      { type: 'playstore', url: LINKS.PVP_MATCH, fallbackUrl: LINKS.MAD_OVER_GAMES },
      { type: 'company', url: LINKS.MAD_OVER_GAMES, label: 'MadOverGames' },
    ],
    visibility: 'public',
    order: 0,
    enabled: true,
  },
  {
    id: 'bubble-shooter-space',
    debugKey: 'BUBBLE_SHOOTER_SPACE',
    title: 'Bubble Shooter Space',
    desc: 'Full-stack development of a top-grossing mobile game reaching 200k+ users with 4.8 App Store rating. Integrated advanced analytics, monetisation strategies, and performance optimization for a 99.85% crash-free rate.',
    tech: ['Unity', 'C#', 'Analytics', 'Monetisation', 'Optimization'],
    links: [
      // { type: 'playstore', url: LINKS.BUBBLE_SHOOTER_SPACE_STORE, fallbackUrl: LINKS.BUBBLE_SHOOTER_SPACE },
      { type: 'appstore', url: LINKS.BUBBLE_SHOOTER_SPACE_IOS, fallbackUrl: LINKS.BUBBLE_SHOOTER_SPACE },
      { type: 'company', url: LINKS.BUBBLE_SHOOTER_SPACE, label: 'MadOverGames' },
    ],
    visibility: 'public',
    order: 1,
    enabled: true,
  },
  {
    id: 'bird-sanctuary-3d',
    debugKey: 'BIRD_SANCTUARY_3D',
    title: '3D Bird Sanctuary',
    desc: 'Immersive educational 3D exploration game teaching biodiversity through play. Built with custom shaders, complex state machines, and an asset-bundle streaming pipeline for WebGL deployment.',
    tech: ['Unity', 'C#', 'WebGL', 'Custom Shaders', 'Asset Bundles'],
    links: [
      { type: 'company', url: LINKS.EXTRAMARKS, label: 'Extramarks' },
    ],
    visibility: 'internal',
    order: 2,
    enabled: true,
  },
  // === Existing public projects (order 3-4) ===
  {
    id: 'superhero-hunter',
    debugKey: 'SUPERHERO_HUNTER',
    title: 'SuperHero Hunter',
    desc: 'A responsive, single-page application consuming the Superhero API. Implemented efficient state, real-time search, async data fetching and persistent favorites via localStorage.',
    tech: ['HTML5', 'CSS3', 'JavaScript', 'REST APIs'],
    links: [
      { type: 'github', url: LINKS.SUPERHERO_HUNTER },
    ],
    visibility: 'public',
    order: 3,
    enabled: true,
  },
  {
    id: 'stopwatch',
    debugKey: 'STOPWATCH',
    title: 'StopWatch',
    desc: 'A high-precision, mobile-responsive stopwatch utility built with vanilla JavaScript. Lap-timing, persistent state, and zero dependencies.',
    tech: ['Vanilla JS', 'CSS3', 'HTML5', 'Responsive'],
    links: [
      { type: 'github', url: LINKS.STOPWATCH },
    ],
    visibility: 'public',
    order: 4,
    enabled: true,
  },
  // === College projects (order 5-8) ===
  {
    id: 'image-recognition-attendance',
    debugKey: 'IMAGE_RECOGNITION',
    title: 'Image Recognition Attendance System',
    desc: 'Automated attendance pipeline using Computer Vision. Built a robust ML model in Python that processes and recognises student faces in real time and writes to a database.',
    tech: ['Python', 'OpenCV', 'Machine Learning', 'Data Pipelines'],
    visibility: 'college',
    badge: 'College Project',
    order: 5,
    enabled: true,
  },
  {
    id: 'csed-vr-walkthrough',
    debugKey: 'CSED_VR',
    title: 'CSED VR Walkthrough',
    desc: 'High-fidelity Virtual Reality tour of the Thapar University CSED department. Optimised 3D assets, lighting and rendering in Unity for low-end VR hardware.',
    tech: ['Blender', 'Unity', 'VR', 'Performance'],
    visibility: 'college',
    badge: 'Capstone',
    order: 6,
    enabled: true,
  },
  {
    id: 'college-predictor',
    debugKey: 'COLLEGE_PREDICTOR',
    title: 'College Predictor System',
    desc: 'Algorithmic platform predicting college admissions from JEE marks. Architected an ER model and used sequential processing to maximise prediction accuracy.',
    tech: ['Database Design', 'ER Modeling', 'Algorithms', 'SQL'],
    visibility: 'college',
    badge: 'College Project',
    order: 7,
    enabled: true,
  },
  {
    id: 'catapult',
    debugKey: 'CATAPULT',
    title: 'Catapult Project',
    desc: 'Top-5 finalist in a national-level design-and-build challenge. Iterated on mechanical efficiency, mass distribution and material science under tight time pressure.',
    tech: ['Mechanics', 'Material Science', 'Teamwork', 'Iterative Design'],
    visibility: 'college',
    badge: 'Top 5 Finalist',
    order: 8,
    enabled: true,
  },
]

/** Returns enabled projects filtered by optional debug overrides, sorted by order or a custom order override. Defaults to the static order field. */
export function getActiveProjects(
  overrides?: Record<string, boolean>,
  orderOverride?: string[] | null,
): ProjectDef[] {
  const result = projectsConfig.filter((p) => {
    if (overrides) {
      return overrides[p.debugKey] !== false
    }
    return p.enabled
  })

  if (orderOverride && orderOverride.length > 0) {
    const orderMap = new Map(orderOverride.map((id, i) => [id, i]))
    result.sort((a, b) => {
      const ai = orderMap.get(a.id)
      const bi = orderMap.get(b.id)
      if (ai !== undefined && bi !== undefined) return ai - bi
      if (ai !== undefined) return -1
      if (bi !== undefined) return 1
      return a.order - b.order
    })
  } else {
    result.sort((a, b) => a.order - b.order)
  }

  return result
}
